import { Form, Formik, FormikProps } from 'formik';
import { FC, SVGProps, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { DataSourceCrmCreateFormModel } from '../../../../types';
import { stashDataSource } from '@/features/dataSources/dataSourcesSlice';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '@/reducers';
import { DataSource, DataSourceCrmConfig, DataSourceIDEnum, DataSourceType, EDataSourceCategory } from '@/models/dataSource';
import { StepPageActions } from '@/global/components';
import {
  CreateDataSourceResponse,
  useCrmDataSourceAuthenticatedMutation,
  useSaveDataSourceMutation,
  useUpdateDataSourceMutation,
} from '@/services/dataSources';
import { useWorkspace } from '@/features/workspaces/hooks';
import { stashFieldDefinitions, stashParameterDefinitions } from '@/features/cyclr/dataSourceIntegrationSlice';
import { CREATE_DATA_SOURCE_CRM } from '../constants';
import { Input, VisualInput } from '@/components/inputs';
import { popupCenter } from '@/utils';
import { RouteConstants } from '@/features/router/RouteConstants';
import { DATA_SOURCE_ICONS } from '@/lib/Icons/DataSourceIcons/constants';

type CrmDataSourceSourceDetailsProps = {
  dataSourceType: DataSourceType;
  onNextStep?: VoidFunction;
  onPrevStep?: VoidFunction;
  onGoToStep?: (id: CREATE_DATA_SOURCE_CRM) => void;
  onCancel?: VoidFunction;
};

export const CrmDataSourceSourceDetailsStep: FC<CrmDataSourceSourceDetailsProps> = ({ dataSourceType, onNextStep, onGoToStep, onCancel }) => {
  const dispatch = useDispatch();
  const workspace = useWorkspace();
  const { t } = useTranslation();

  const dataSourceTypeId = dataSourceType.id as DataSourceIDEnum;
  const RawDataSourceTypeIcon = DATA_SOURCE_ICONS[dataSourceTypeId];
  const DataSourceTypeIcon = (props: SVGProps<SVGSVGElement>) => <RawDataSourceTypeIcon className={props.className} />;
  const dataSource = useAppSelector((state) => state.dataSources.edit?.dataSource) as Partial<DataSource<DataSourceCrmConfig>> | undefined;

  const dataSourceInfoFormRef = useRef<FormikProps<DataSourceCrmCreateFormModel>>(null);

  const validationSchema = Yup.object({
    name: Yup.string()
      .min(3, t('data_source_edit:validations.too_short'))
      .max(50, t('data_source_edit:validations.too_long'))
      .required(t('data_source_edit:validations.required')),
    configuration: Yup.object({}),
  });

  const retrieveLabelForDataSourceType = (dataSourceType: DataSourceType) => {
    switch (dataSourceType?.category) {
      case EDataSourceCategory.CRM:
        return dataSourceType.name;
      case EDataSourceCategory.CODE:
      case EDataSourceCategory.FILE:
      case EDataSourceCategory.OTHER:
      default:
        return 'TBD';
    }
  };

  const [saveDataSource, { isLoading: isSaveLoading, data: createdDataSource }] = useSaveDataSourceMutation();
  const [updateDataSource, { isLoading: isUpdateLoading, data: updatedDataSource }] = useUpdateDataSourceMutation();
  const [dataSourceAuthenticated, { isLoading: isAuthenticatedLoading, data: authenticatedDataSource }] = useCrmDataSourceAuthenticatedMutation();

  const isLoading = isSaveLoading || isUpdateLoading || isAuthenticatedLoading;

  useEffect(() => {
    if (createdDataSource) {
      dispatch(stashDataSource(createdDataSource.dataSource));

      if (createdDataSource.integrationsResult?.cyclr.action.authUrl) {
        authenticationScreen(createdDataSource);
      } else if (createdDataSource.integrationsResult?.cyclr.action.parameters?.length) {
        dispatch(stashParameterDefinitions(createdDataSource.integrationsResult.cyclr.action.parameters));
        onNextStep();
      } else if (createdDataSource.integrationsResult?.cyclr.action.fields?.length) {
        dispatch(stashFieldDefinitions(createdDataSource.integrationsResult.cyclr.action.fields));
        onGoToStep(CREATE_DATA_SOURCE_CRM.UPDATE_FIELDS);
      } else {
        onGoToStep(CREATE_DATA_SOURCE_CRM.CHECK_INSTALLATION);
      }
    }
  }, [createdDataSource]);

  useEffect(() => {
    if (updatedDataSource) {
      dispatch(stashDataSource(updatedDataSource));

      if (updatedDataSource.configuration && (updatedDataSource.configuration as any).cyclr?.status === 'ACTIVE') {
        onGoToStep(CREATE_DATA_SOURCE_CRM.INSTALLATION_SUCCESS);
      } else {
        onNextStep();
      }
    }
  }, [updatedDataSource]);

  useEffect(() => {
    if (authenticatedDataSource) {
      dispatch(stashDataSource(authenticatedDataSource.dataSource));

      if (authenticatedDataSource.integrationsResult?.cyclr.action.parameters?.length) {
        dispatch(stashParameterDefinitions(authenticatedDataSource.integrationsResult.cyclr.action.parameters));
        onNextStep();
      } else if (authenticatedDataSource.integrationsResult?.cyclr.action.fields?.length) {
        dispatch(stashFieldDefinitions(authenticatedDataSource.integrationsResult.cyclr.action.fields));
        onGoToStep(CREATE_DATA_SOURCE_CRM.UPDATE_FIELDS);
      } else {
        onGoToStep(CREATE_DATA_SOURCE_CRM.CHECK_INSTALLATION);
      }
    }
  }, [authenticatedDataSource]);

  const handleSubmit = async (form: DataSourceCrmCreateFormModel) => {
    if (form.name) {
      dispatch(
        stashDataSource({
          ...form,
          name: form.name,
          configuration: { ...form.configuration },
          type: dataSourceType,
        })
      );
    }
  };

  const createOrUpdateDataSource = (dataSource: Partial<DataSource<DataSourceCrmConfig>>) => {
    if (dataSource.name) {
      if (!dataSource.id) {
        saveDataSource({
          workspaceId: workspace.id,
          dataSource: {
            type: dataSourceType.id,
            name: dataSource.name,
            configuration: { ...dataSource.configuration },
          },
        });
      } else {
        updateDataSource({
          workspaceId: workspace.id,
          dataSourceId: dataSource.id,
          dataSource: {
            name: dataSource.name,
            configuration: { ...dataSource.configuration },
          },
        });
      }
    }
  };

  const authenticationScreen = (createdDataSource: CreateDataSourceResponse) => {
    (window as any).cyclrAuthenticationCompleted = (success: boolean, w: Window) => {
      w.close.apply(w);
      delete (window as any).cyclrAuthenticationCompleted;

      dataSourceAuthenticated({
        workspaceId: workspace.id,
        dataSourceId: createdDataSource.dataSource.id,
      });
    };

    const action = createdDataSource.integrationsResult.cyclr.action;
    const connector = action.accountConnectors?.length ? action.accountConnectors[0] : '';
    const url = `${action.authUrl}?id=${connector}&token=${action.token}&targetOrigin=${location.origin + RouteConstants.cyclrAuthRedirect}`;

    (window as any).authWindow = popupCenter({
      url,
      title: t('authenticate'),
      w: 640,
      h: 640,
    });
  };

  const handleNextStep = async () => {
    await dataSourceInfoFormRef.current?.submitForm();

    if (dataSourceInfoFormRef.current?.isValid && dataSourceInfoFormRef.current?.values) {
      createOrUpdateDataSource(dataSourceInfoFormRef.current?.values);
    }
  };

  return (
    <>
      <Formik
        innerRef={dataSourceInfoFormRef}
        initialValues={{ ...dataSource }}
        onSubmit={handleSubmit}
        validateOnBlur={false}
        validateOnChange={false}
        validationSchema={validationSchema}
      >
        {({ values, errors, handleChange, setFieldError }) => {
          return (
            <Form>
              <VisualInput
                name='type'
                id='type'
                label={t('data_source_edit:crm.source_details.source_type')}
                value={retrieveLabelForDataSourceType(dataSourceType)}
                icon={DataSourceTypeIcon}
              />
              <Input
                name='name'
                id='name'
                type='text'
                label={t('data_source_edit:crm.source_details.source_name')}
                value={values.name}
                placeholder={t('data_source_edit:crm.source_details.source_name_placeholder')}
                onChange={(e) => {
                  setFieldError('name', undefined);
                  handleChange('name')(e.target.value);
                }}
                error={errors.name}
              />
            </Form>
          );
        }}
      </Formik>

      <StepPageActions
        primaryButton={{
          text: 'COMMON:confirm',
          onClick: handleNextStep,
          isLoading: isLoading,
        }}
        secondaryButton={{
          text: 'COMMON:cancel',
          onClick: onCancel,
        }}
      />
    </>
  );
};
